import React, { useState, useMemo, useCallback, useEffect, useRef } from 'react';
import {
  TextField,
  InputAdornment,
  Tooltip,
  Typography,
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import FolderIcon from '@mui/icons-material/Folder';
import CategoryIcon from '@mui/icons-material/Category';
import BarChartIcon from '@mui/icons-material/BarChart';
import ViewColumnIcon from '@mui/icons-material/ViewColumn';
import AccountTreeIcon from '@mui/icons-material/AccountTree';
import LayersIcon from '@mui/icons-material/Layers';
import PersonIcon from '@mui/icons-material/Person';
import StoreIcon from '@mui/icons-material/Store';
import LoyaltyIcon from '@mui/icons-material/Loyalty';
import AttachMoneyIcon from '@mui/icons-material/AttachMoney';
import PercentIcon from '@mui/icons-material/Percent';
import TagIcon from '@mui/icons-material/Tag';
import CalendarMonthIcon from '@mui/icons-material/CalendarMonth';
import CalendarTodayIcon from '@mui/icons-material/CalendarToday';
import LabelIcon from '@mui/icons-material/Label';
import ToggleOnIcon from '@mui/icons-material/ToggleOn';
import DataObjectIcon from '@mui/icons-material/DataObject';
import { useVoyagerStore } from '../../stores/useVoyagerStore';
import { TreeNode } from '../../types';
import { filterTreeNodes } from '../../utils';
import {
  TreeContainer,
  SearchContainer,
  TreeListContainer,
  TreeItemRowStyled,
  ExpandIconWrapper,
  NodeIconWrapper,
  NodeLabel,
  EmptyTreeMessage,
} from './TreeView.styles';

// ===== Helper: get icon based on node type and dataType =====
function getNodeIcon(node: TreeNode) {
  switch (node.type) {
    case 'domain':
      return <FolderIcon fontSize="small" color="primary" />;
    case 'group':
      return <CategoryIcon fontSize="small" color="action" />;
    case 'metric':
      switch (node.dataType) {
        case 'currency': return <AttachMoneyIcon fontSize="small" sx={{ color: 'var(--ok)' }} />;
        case 'percentage': return <PercentIcon fontSize="small" sx={{ color: 'var(--warn)' }} />;
        case 'integer':
        case 'number': return <TagIcon fontSize="small" sx={{ color: 'var(--accent)' }} />;
        default: return <BarChartIcon fontSize="small" sx={{ color: 'var(--accent)' }} />;
      }
    case 'dimension':
      switch (node.dataType) {
        case 'date': return <CalendarMonthIcon fontSize="small" sx={{ color: 'var(--accent)' }} />;
        case 'boolean': return <ToggleOnIcon fontSize="small" sx={{ color: 'var(--agent)' }} />;
        case 'json': return <DataObjectIcon fontSize="small" sx={{ color: 'var(--agent)' }} />;
        case 'categorical': return <LabelIcon fontSize="small" sx={{ color: 'var(--agent)' }} />;
        default: return <ViewColumnIcon fontSize="small" sx={{ color: 'var(--agent)' }} />;
      }
    case 'date_hierarchy':
      return <CalendarTodayIcon fontSize="small" sx={{ color: 'var(--accent)' }} />;
    case 'hierarchy':
      return <LayersIcon fontSize="small" sx={{ color: 'var(--accent)' }} />;
    case 'inherited_model':
      switch (node.metadata?.icon) {
        case 'person': return <PersonIcon fontSize="small" sx={{ color: 'var(--ok)' }} />;
        case 'store': return <StoreIcon fontSize="small" sx={{ color: 'var(--ok)' }} />;
        case 'loyalty': return <LoyaltyIcon fontSize="small" sx={{ color: 'var(--ok)' }} />;
        default: return <AccountTreeIcon fontSize="small" sx={{ color: 'var(--ok)' }} />;
      }
    default:
      return <AccountTreeIcon fontSize="small" />;
  }
}

// ===== Helper: extract the raw name from an ID like "metric:revenue" =====
function extractName(nodeId: string): string {
  const colonIdx = nodeId.indexOf(':');
  return colonIdx >= 0 ? nodeId.substring(colonIdx + 1) : nodeId;
}

// ===== Recursive TreeNode renderer =====
interface TreeItemProps {
  node: TreeNode;
  depth: number;
}

const TreeItem: React.FC<TreeItemProps> = React.memo(({ node, depth }) => {
  const expandedNodes = useVoyagerStore((s) => s.expandedNodes);
  const focusedNodeId = useVoyagerStore((s) => s.focusedNodeId);
  const selectedMetrics = useVoyagerStore((s) => s.selectedMetrics);
  const selectedDimensions = useVoyagerStore((s) => s.selectedDimensions);
  const navigateToNode = useVoyagerStore((s) => s.navigateToNode);
  const toggleMetric = useVoyagerStore((s) => s.toggleMetric);
  const toggleDimension = useVoyagerStore((s) => s.toggleDimension);

  const itemRef = useRef<HTMLDivElement>(null);

  const hasChildren = node.children && node.children.length > 0;
  const isExpanded = expandedNodes.has(node.id);
  const isFocused = focusedNodeId === node.id;
  const isMetric = node.type === 'metric';
  const isDimension = node.type === 'dimension';
  const isDateHierarchy = node.type === 'date_hierarchy';
  const isSelectable = isMetric || isDimension || isDateHierarchy;

  const nodeName = node.metadata?.name || extractName(node.id);
  const isSelected = isMetric
    ? selectedMetrics.includes(nodeName)
    : (isDimension || isDateHierarchy)
      ? selectedDimensions.includes(nodeName)
      : false;

  // Auto-scroll to focused node
  useEffect(() => {
    if (isFocused && itemRef.current) {
      itemRef.current.scrollIntoView({ behavior: 'smooth', block: 'center' });
    }
  }, [isFocused]);

  const handleToggleExpand = useCallback(
    (e: React.MouseEvent) => {
      e.stopPropagation();
      if (hasChildren) {
        // Toggle expand through store
        useVoyagerStore.getState().navigateToNode(node.id);
        // We use a simpler approach: directly toggle via expandedNodes
        const expanded = useVoyagerStore.getState().expandedNodes;
        const newExpanded = new Set(expanded);
        if (newExpanded.has(node.id)) {
          newExpanded.delete(node.id);
        } else {
          newExpanded.add(node.id);
        }
        useVoyagerStore.setState({ expandedNodes: newExpanded });
      }
    },
    [hasChildren, node.id]
  );

  const handleClick = useCallback(() => {
    if (isMetric) {
      toggleMetric(nodeName);
      navigateToNode(node.id);
    } else if (isDateHierarchy) {
      toggleDimension(nodeName);
      navigateToNode(node.id);
    } else if (isDimension) {
      // All dimensions are selectable (including date dims with children)
      toggleDimension(nodeName);
      navigateToNode(node.id);
    } else if (hasChildren) {
      // For non-selectable expandable nodes (groups, domain), toggle expand
      const expanded = useVoyagerStore.getState().expandedNodes;
      const newExpanded = new Set(expanded);
      if (newExpanded.has(node.id)) {
        newExpanded.delete(node.id);
      } else {
        newExpanded.add(node.id);
      }
      useVoyagerStore.setState({ expandedNodes: newExpanded });
    }
  }, [isMetric, isDimension, isDateHierarchy, hasChildren, node.id, nodeName, navigateToNode, toggleMetric, toggleDimension]);

  const tooltipContent = node.metadata?.description
    ? `${node.metadata.description}${
        node.metadata.use_cases?.length
          ? `\nUse cases: ${node.metadata.use_cases.join(', ')}`
          : ''
      }`
    : '';

  return (
    <>
      <Tooltip
        title={tooltipContent}
        placement="right"
        arrow
        disableHoverListener={!tooltipContent}
        enterDelay={500}
      >
        <TreeItemRowStyled
          ref={itemRef}
          depth={depth}
          focused={isFocused}
          selected={isSelected}
          onClick={handleClick}
        >
          {/* Expand/collapse arrow */}
          {hasChildren ? (
            <ExpandIconWrapper expanded={isExpanded} onClick={handleToggleExpand}>
              <ChevronRightIcon fontSize="small" />
            </ExpandIconWrapper>
          ) : (
            <ExpandIconWrapper expanded={false} sx={{ visibility: 'hidden' }}>
              <ChevronRightIcon fontSize="small" />
            </ExpandIconWrapper>
          )}

          {/* Node icon */}
          <NodeIconWrapper>{getNodeIcon(node)}</NodeIconWrapper>

          {/* Node label */}
          <NodeLabel>
            <Typography
              variant="body2"
              component="span"
              sx={{
                fontWeight: node.type === 'domain' || node.type === 'group' || node.type === 'inherited_model' || node.type === 'hierarchy' ? 600 : 400,
              }}
            >
              {node.label}
            </Typography>
          </NodeLabel>
        </TreeItemRowStyled>
      </Tooltip>

      {/* Render children if expanded */}
      {hasChildren && isExpanded && (
        <>
          {node.children!.map((child) => (
            <TreeItem key={child.id} node={child} depth={depth + 1} />
          ))}
        </>
      )}
    </>
  );
});

TreeItem.displayName = 'TreeItem';

// ===== Main TreeView Component =====
const TreeView: React.FC = () => {
  const treeNodes = useVoyagerStore((s) => s.treeNodes);
  const semanticModel = useVoyagerStore((s) => s.semanticModel);
  const [searchTerm, setSearchTerm] = useState('');

  const filteredNodes = useMemo(
    () => filterTreeNodes(treeNodes, searchTerm),
    [treeNodes, searchTerm]
  );

  return (
    <TreeContainer>
      {/* Search input */}
      <SearchContainer>
        <TextField
          size="small"
          fullWidth
          placeholder="Search metrics & dimensions..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon fontSize="small" color="action" />
              </InputAdornment>
            ),
          }}
          sx={{
            '& .MuiOutlinedInput-root': {
              fontSize: '0.85rem',
            },
          }}
        />
      </SearchContainer>

      {/* Tree list */}
      <TreeListContainer>
        {!semanticModel ? (
          <EmptyTreeMessage>
            <AccountTreeIcon sx={{ fontSize: 48, mb: 1, opacity: 0.3 }} />
            <Typography variant="body2">
              Select a domain and perspective to browse the semantic model
            </Typography>
          </EmptyTreeMessage>
        ) : filteredNodes.length === 0 ? (
          <EmptyTreeMessage>
            <SearchIcon sx={{ fontSize: 36, mb: 1, opacity: 0.3 }} />
            <Typography variant="body2">
              No results found for "{searchTerm}"
            </Typography>
          </EmptyTreeMessage>
        ) : (
          filteredNodes.map((node) => (
            <TreeItem key={node.id} node={node} depth={0} />
          ))
        )}
      </TreeListContainer>
    </TreeContainer>
  );
};

export default TreeView;
